// Social in-app browsers (CONTRACT-FIX1). Google refuses OAuth inside embedded
// webviews (`disallowed_useragent`), so on a shared question opened from
// Instagram, Facebook, X, LinkedIn, TikTok, Snapchat or LINE the drawer defaults
// to Phone sign-in and offers an escape to Safari/Chrome that carries the share
// token. /login does the same for a visitor an ad (Reddit, Meta) sent there,
// with an escape that keeps its ?next= return and campaign keys. Detection only
// picks a default and some copy: both tabs stay reachable, and a wrong guess
// costs one tap.
import { AD_CLICK_URL_ONLY_KEYS, buildPathFromSafeQuery, getSafeNextPath } from './attribution';
import { PRACTICE_ABSITE_PATH, isPracticeEntryPath } from './absite-practice-intent';
import { POST_LOGIN_PATHS } from '../constants/postLoginPaths';
import { QUIZ_LINK_OPAQUE_PATH } from './url-secret-scrub';
import { gtmTrack } from './tracking';
import { isNativeCapacitorRuntime } from '../services/revenueCatService';

// Order matters: an app token wins over the generic Android `; wv)` and iOS
// webview rules below. A string token matches anywhere in the User-Agent; a
// RegExp token is for a name that could also appear inside a URL.
const APP_TOKENS = [
  ['instagram', ['Instagram']],
  ['facebook', ['FBAN/', 'FBAV/', 'FB_IAB', 'FBIOS']],
  ['linkedin', ['LinkedInApp']],
  ['x', ['Twitter for iPhone', 'TwitterAndroid']],
  ['tiktok', ['musical_ly', 'BytedanceWebview', 'TikTok']],
  ['snapchat', ['Snapchat']],
  ['line', [' Line/']],
  // Reported, not yet captured from a device: Threads identifies as "Barcelona".
  ['threads', ['Barcelona']],
  ['pinterest', ['Pinterest']],
  // Reported, not yet captured from a device: Reddit's app may add a
  // "Reddit/<version>" or a "RedditApp" token. Case-sensitive and only as a
  // token of its own (start of the string, or after a space, ";", "(" or "["),
  // so a "reddit.com/" or ".../Reddit/..." URL in a User-Agent never counts.
  ['reddit', [/(?:^|[\s;([])(?:Reddit\/|RedditApp\b)/]],
];

const matchesToken = (ua, token) => (
  token instanceof RegExp ? token.test(ua) : ua.includes(token)
);

const detectOs = (ua) => {
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
};

const safeIsNative = () => {
  try {
    return isNativeCapacitorRuntime();
  } catch (e) {
    return false;
  }
};

// An iOS home-screen web app (Add to Home Screen) has the same User-Agent as
// an embedded WKWebView (no "Safari/"), but it is Safari: Google sign-in works.
const safeIsStandalone = () => {
  try {
    return typeof navigator !== 'undefined' && navigator.standalone === true;
  } catch (e) {
    return false;
  }
};

// Touch points tell an iPad (5) from a Mac (0) when both send the Mac
// User-Agent; 0 when the page cannot say.
const safeMaxTouchPoints = () => {
  try {
    return typeof navigator !== 'undefined' && typeof navigator.maxTouchPoints === 'number'
      ? navigator.maxTouchPoints
      : 0;
  } catch (e) {
    return 0;
  }
};

// The first app in APP_TOKENS whose token the User-Agent carries, or null.
const findTokenApp = (ua) => {
  const match = APP_TOKENS.find(([, tokens]) => tokens.some((token) => matchesToken(ua, token)));
  return match ? match[0] : null;
};

// A webview with no app token, or null. Checked after the app tokens.
const detectTokenlessWebview = (ua, os, isIpadWithMacUa) => {
  if (os === 'android' && ua.includes('; wv)')) {
    return { inApp: true, app: 'android_webview', os };
  }
  // An app's embedded WKWebView: WebKit without the "Safari/" token. Safari,
  // SFSafariViewController and the iOS browsers (Chrome CriOS, Firefox FxiOS,
  // Edge EdgiOS, DuckDuckGo, the Google app GSA) all carry "Safari/60x".
  const webKitWithoutSafari = ua.includes('AppleWebKit') && !ua.includes('Safari/');
  if (os === 'ios' && webKitWithoutSafari) {
    return { inApp: true, app: 'ios_webview', os };
  }
  // The same webview on an iPad, with the Mac User-Agent ("Macintosh", no
  // "Safari/"). A Mac app's own WebKit view sends that User-Agent too, but
  // has no touch points. Safari on a Mac, and on an iPad in desktop mode,
  // carries "Safari/"; the native app and the home-screen web app returned
  // before this is called.
  if (isIpadWithMacUa && webKitWithoutSafari) {
    return { inApp: true, app: 'ios_webview', os: 'ios' };
  }
  return null;
};

// Reads the User-Agent only, never the referrer: a visitor who came from
// reddit.com in a real browser is in a real browser.
export const detectInAppBrowser = (
  ua = typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  {
    isNative = safeIsNative(),
    isStandalone = safeIsStandalone(),
    maxTouchPoints = safeMaxTouchPoints(),
  } = {}
) => {
  if (typeof ua !== 'string' || !ua) {
    return { inApp: false, app: null, os: 'other' };
  }
  const os = detectOs(ua);
  if (isNative || isStandalone) {
    return { inApp: false, app: null, os };
  }
  // iPadOS asks for desktop sites with the Mac User-Agent, in Safari and, by
  // default, in an app's WKWebView. Touch points tell an iPad from a Mac.
  const isIpadWithMacUa = /Macintosh/.test(ua) && maxTouchPoints > 1;
  const app = findTokenApp(ua);
  if (app) {
    // An iPad app that adds its own token to the Mac User-Agent is still on
    // iOS: its copy says Safari, not "your browser".
    return { inApp: true, app, os: isIpadWithMacUa ? 'ios' : os };
  }
  return detectTokenlessWebview(ua, os, isIpadWithMacUa) || { inApp: false, app: null, os };
};

// What the sign-in copy calls each detected app ("Google sign-in doesn't work
// inside Reddit"). A webview with no app token (android_webview, ios_webview)
// has no name to give: "inside this app".
const IN_APP_DISPLAY_NAMES = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  x: 'X',
  tiktok: 'TikTok',
  snapchat: 'Snapchat',
  line: 'LINE',
  threads: 'Threads',
  pinterest: 'Pinterest',
  reddit: 'Reddit',
};

export const inAppBrowserDisplayName = (app) => (
  Object.prototype.hasOwnProperty.call(IN_APP_DISPLAY_NAMES, app)
    ? IN_APP_DISPLAY_NAMES[app]
    : 'this app'
);

// The app's own browser, by the same names ("inside Reddit's browser",
// "inside this app's browser"). A name ending in "s" takes only the
// apostrophe: "Threads' browser".
export const inAppBrowserLabel = (app) => {
  const name = inAppBrowserDisplayName(app);
  return `${name}${name.endsWith('s') ? "'" : "'s"} browser`;
};

// The current question URL with only allowlisted attribution, plus the share
// token so the page opens in a fresh browser (the token-less URL is a dead end
// there: AUDIT #2). Never "token=" or "token=null". `urlOnlyParamKeys` (the
// ad click ids) is for the sign-in page's escape below; the question escape
// passes none.
export const buildEscapeUrl = ({
  location = typeof window !== 'undefined' ? window.location : undefined,
  shareToken,
  urlOnlyParamKeys = [],
} = {}) => {
  const path = buildPathFromSafeQuery(location?.pathname, location?.search, { urlOnlyParamKeys });
  const url = new URL(path, location?.origin);
  // Always https: the escape opens a real browser on the public site, and the
  // Android intent only accepts an https target.
  url.protocol = 'https:';
  const token = typeof shareToken === 'string' ? shareToken.trim() : '';
  if (token && token !== 'null' && token !== 'undefined') {
    url.searchParams.set('token', token);
  }
  return url.toString();
};

// Where /login's ?next= may send the visitor once the page reopens in a real
// browser: the destination /login itself would honor (its allowlist), as a
// path only. The ABSITE practice entry is the one return with a query, and it
// is rebuilt from the constant, never copied, so nothing else that rode in
// ?next= (a token included) comes along. A quiz link's token-free return
// (/notes-to-quiz/claim, exactly) is kept as the constant: the claim token
// stays in this tab, but there /login can still say to reopen the email link.
const loginEscapeNextPath = (search) => {
  if (getSafeNextPath(search, { allowedPathnames: [QUIZ_LINK_OPAQUE_PATH] }) === QUIZ_LINK_OPAQUE_PATH) {
    return QUIZ_LINK_OPAQUE_PATH;
  }
  const next = getSafeNextPath(search, { allowedPathnames: POST_LOGIN_PATHS });
  if (!next) {
    return undefined;
  }
  if (isPracticeEntryPath(next)) {
    return PRACTICE_ABSITE_PATH;
  }
  return new URL(next, 'https://qlearn.local').pathname;
};

// The sign-in page itself, for the same escape: the address /login keeps
// (App.jsx's clean-up: allowlisted campaign keys, ad click ids, ?next=), so
// the real browser records the same first touch and, after sign-in, returns
// the visitor where ?next= says. No share token, and nothing else from the
// address: not ?auth=, the Daily return's ids or a quiz link's claim token.
export const buildLoginEscapeUrl = ({
  location = typeof window !== 'undefined' ? window.location : undefined,
} = {}) => {
  const url = new URL(buildEscapeUrl({ location, urlOnlyParamKeys: AD_CLICK_URL_ONLY_KEYS }));
  const next = loginEscapeNextPath(location?.search);
  if (next) {
    url.searchParams.set('next', next);
  }
  return url.toString();
};

// Android: hand the https URL to Chrome; if Chrome is missing, Android opens
// the fallback URL in the default browser.
export const buildAndroidChromeIntent = (httpsUrl) => {
  let parsed;
  try {
    parsed = new URL(httpsUrl);
  } catch (e) {
    return null;
  }
  if (parsed.protocol !== 'https:') {
    return null;
  }
  return `intent://${parsed.host}${parsed.pathname}${parsed.search}`
    + `#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(parsed.toString())};end`;
};

// A navigation (not an <a href>): link-click listeners such as GA4 enhanced
// measurement never see the tokenized URL.
export const navigateToEscapeUrl = (target) => {
  window.location.href = target;
};

// Exactly these four fields; never the URL, token, qlinkId or user id.
export const trackInAppBrowserEscape = ({ app, os, action, platform } = {}) => {
  try {
    gtmTrack('qlink_inapp_escape', { app, os, action, platform });
  } catch (e) {
    // Tracking must never break the escape.
  }
};
